import { useMutation } from "@tanstack/react-query";
import { KeyRound, LogOut } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { isClientApiError } from "../../api/http.js";
import { useSession } from "../../app/AppProviders.js";
import {
  LOGIN_PATH,
  RETURN_PARAM,
  safeReturnPath,
} from "../../app/RequireSession.js";
import { roleHomePath } from "../../app/RoleHomeRedirect.js";
import { describeError, signOutFailureMessage } from "../../ui/AsyncPanel.js";
import { Button, ControlSizeProvider } from "../../ui/Button.js";
import { TextField } from "../../ui/FormField.js";
import { PASSWORD_MIN_LENGTH, passwordLengthError } from "./password-rules.js";

/**
 * Mandatory replacement of a temporary password. Reached only through the
 * session guard: a signed-in person whose account still carries a temporary
 * password is sent here from every other route, and a person who does not
 * need to change anything is sent straight back to their home.
 */

type PasswordFieldErrors = {
  currentPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
};

function validate(values: {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}): PasswordFieldErrors {
  const errors: PasswordFieldErrors = {};
  if (!values.currentPassword) {
    errors.currentPassword = "Enter the temporary password you were given.";
  }
  const length = passwordLengthError(values.newPassword);
  if (length) {
    errors.newPassword = length;
  } else if (values.newPassword === values.currentPassword) {
    errors.newPassword =
      "Choose a password that is different from the temporary one.";
  }
  if (values.confirmPassword !== values.newPassword) {
    errors.confirmPassword =
      "The passwords don't match. Type the new password again.";
  }
  return errors;
}

function serverFieldErrors(error: unknown): PasswordFieldErrors {
  if (!isClientApiError(error)) return {};
  // A wrong current password comes back as INVALID_CREDENTIALS — a 401, the
  // same error a failed login raises — so the code decides, not the status.
  // A 401 with any other code really is a session that ended.
  if (
    error.status >= 400 &&
    error.status < 500 &&
    error.code === "INVALID_CREDENTIALS"
  ) {
    return { currentPassword: "The temporary password is incorrect." };
  }
  const [currentPassword] = error.fieldErrors["currentPassword"] ?? [];
  const [newPassword] = error.fieldErrors["newPassword"] ?? [];
  return { currentPassword, newPassword };
}

export function ForcePasswordChangePage() {
  const { user, changePassword, logout } = useSession();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const next = safeReturnPath(searchParams.get(RETURN_PARAM));

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState<PasswordFieldErrors>({});

  const save = useMutation({
    mutationFn: changePassword,
    onSuccess: (updated) => {
      navigate(next ?? roleHomePath(updated.role), { replace: true });
    },
  });

  const signOut = useMutation({
    mutationFn: logout,
    onSuccess: () => {
      navigate(LOGIN_PATH, { replace: true });
    },
  });

  if (!user) {
    return <Navigate to={LOGIN_PATH} replace />;
  }
  if (!user.mustChangePassword) {
    return <Navigate to={next ?? roleHomePath(user.role)} replace />;
  }

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const errors = validate({ currentPassword, newPassword, confirmPassword });
    setFieldErrors(errors);
    save.reset();
    if (Object.keys(errors).length > 0) return;
    save.mutate({ currentPassword, newPassword });
  };

  const fromServer = serverFieldErrors(save.error);
  const showFormAlert =
    save.isError && !fromServer.currentPassword && !fromServer.newPassword;
  const busy = save.isPending || signOut.isPending;

  return (
    <ControlSizeProvider size="touch">
      <main className="auth-page">
        <section className="auth-card" aria-labelledby="change-password-title">
          <header className="auth-card__header">
            <p className="auth-card__brand">Chicagoland Auto Tools</p>
            <h1 id="change-password-title" className="auth-card__title">
              Choose a new password
            </h1>
            <p className="auth-card__lead">
              Hi {user.displayName}. Your temporary password has to be replaced
              before you continue.
            </p>
          </header>

          <form className="auth-form" onSubmit={onSubmit} noValidate>
            {showFormAlert ? (
              <div role="alert" className="form-alert">
                {describeError(save.error).description}
              </div>
            ) : null}
            {signOut.isError ? (
              <div role="alert" className="form-alert">
                {signOutFailureMessage(signOut.error)}
              </div>
            ) : null}

            <TextField
              id="current-password"
              label="Temporary password"
              type="password"
              name="currentPassword"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              error={
                fieldErrors.currentPassword ??
                fromServer.currentPassword ??
                null
              }
              autoFocus
            />

            <TextField
              id="new-password"
              label="New password"
              type="password"
              name="newPassword"
              autoComplete="new-password"
              hint={`At least ${PASSWORD_MIN_LENGTH} characters. A short sentence you will remember works well.`}
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              error={fieldErrors.newPassword ?? fromServer.newPassword ?? null}
            />

            <TextField
              id="confirm-password"
              label="Confirm new password"
              type="password"
              name="confirmPassword"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              error={fieldErrors.confirmPassword ?? null}
            />

            <div className="auth-form__actions">
              <Button
                type="submit"
                variant="primary"
                busy={save.isPending}
                disabled={busy}
                icon={<KeyRound />}
                className="auth-form__submit"
              >
                Save new password
              </Button>
              <Button
                type="button"
                variant="quiet"
                busy={signOut.isPending}
                disabled={busy}
                icon={<LogOut />}
                onClick={() => signOut.mutate()}
              >
                Sign out
              </Button>
            </div>
          </form>
        </section>
      </main>
    </ControlSizeProvider>
  );
}
